// crafting-start.ts — the CRAFT START decided in the browser (Wizard v9 WP6, owner 2026-10-09: "port the crafting roll lane").
//
// A recipe (`roll_type` = Crafting Roll, no variable_1) used to start in PHP: crafting_roll()'s start arm
// (includes/functions/janus/rolls/ability_minigame_rolls.php) gated it, built the plan (ft_crafting_build_plan), resolved and consumed
// the reagent stacks (delete_reagents → ft_reagent_stock_resolution), and minted the WIP shell + tracker + first worker
// (ft_crafting_wip_start). The RULES now live here, transliterated in PHP's order; activity_truth_commit_inc.php
// authorizes from the actor row, CASes the snapshots, and PERSISTS what this decided — the named
// inventory rows consumed at the named quantities, the plan written onto the tracker. PHP re-decides nothing.
//
// Not here: the reagent / tool / cast-grant gate (ability-preconditions.ts — runs first, as it always did) and the completion roll
// (crafting-activity.ts composes settlement, the completion roll, outputs/salvage and worker retirement).
// Out of combat the start spends no AP (process_action_points is a no-op there), so nothing is spent here.
//
// crafting_waives_nonmagical_reagents (Universal Crafter): every untemplated requirement is waived — not resolved, not consumed, and
// not written to the tracker's manifest (so a failed completion's salvage can never refund what was never consumed).
import contract from '../compile/c1-contract.json';
import { craftingPlan, reagentTemplateMatches, withinReach, type AbilityPreconditionReaders, type CraftManifestRow, type CraftPlan } from './ability-preconditions';
import { craftingWaivesNonmagicalReagents, waiveNonmagicalRequirements } from './crafting-waiver';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;

/** RollType crafting_roll (Crafting Roll). */
export const ROLL_CRAFTING = '54DC30E0-923C-4BBF-A8CC-570B0068E8A2';
/** StatusEffect 'crafting' — the worker row a craft in progress holds. */
export const STATUS_CRAFTING = '62ED7394-9C33-D41A-C426-1BC6723C999A';

export const CRAFT_START_MESSAGES = {
  inCombat: 'You cannot begin crafting while in combat.',
  alreadyCrafting: 'You are already working on a craft. Stop working on it from its item card before starting another.',
  untrained: 'You are not trained to the skill tier this recipe requires.',
  retired: 'This recipe has been retired and can no longer be started.',
  campaign: 'Crafting requires a campaign context.',
  time: 'This recipe has no valid crafting time authored — it cannot be started. Please report it.',
  manifest: 'This recipe has a malformed reagent list — it cannot be started. Please report it.',
  size: 'That size is not available for this recipe.',
  output: 'This recipe has no output authored (or an unusable one) — it cannot be started. Please report it.',
} as const;

export type CraftStartReaders = AbilityPreconditionReaders & { entries: NonNullable<AbilityPreconditionReaders['entries']>;
  inventoryField: NonNullable<AbilityPreconditionReaders['inventoryField']> };

export interface CraftStartInput {
  /** The crafter's full row (inventory tree, compiled skills, statuses, layers) with its live position. */
  caster: Row;
  /** The recipe definition. */
  recipe: Row;
  /** The crafter's encounter (combat state, the floor stacks); null when the page does not hold it. */
  encounter: Row | null;
  /** use_ability_variable_1 — a craft START has none. */
  variable1?: unknown;
  /** use_ability_variable_2 — the requested SizeType rung (raw), or nothing for the default craft. */
  requestedSize?: unknown;
}

/** One stack the start consumes: the exact inventory row, how many, and which requirement it pays (for the audit). */
export interface CraftConsumeRow { inventory_id: string; qty: number; item_uuid: string; template_id: string | null; floor: boolean }

export type CraftStartVerdict =
  | { kind: 'refuse'; message: string }
  | { kind: 'handback'; reason: string }
  | { kind: 'start'; plan: CraftPlan; consume: CraftConsumeRow[]; waived: CraftManifestRow[] };

/** Whether a use of this ability is a craft START (crafting_roll's empty-variable_1 arm). */
export function isCraftStart(read: Pick<AbilityPreconditionReaders, 'value'>, ability: Row | null, variable1?: unknown): boolean {
  if (!ability || upper(read.value(ability, 'roll_type')) !== ROLL_CRAFTING) return false;
  return variable1 === undefined || variable1 === null || variable1 === '' || variable1 === 0 || variable1 === '0' || variable1 === false;
}

const SKILL_VAR_BY_ID: ReadonlyMap<string, string> = new Map(
  (contract as { rollup: { skillEntries: Array<{ item_id: string; variable_name: string }> } }).rollup.skillEntries
    .map(entry => [upper(entry.item_id), String(entry.variable_name)] as const));

/** check_skill_requirements(): every requires_skill_tier_N must be met by one listed skill at tier ≥ N (a missing compiled entry is tier
 *  0). null = the page holds no compiled skills. */
export function skillRequirementsMet(read: CraftStartReaders, recipe: Row, caster: Row): boolean | null {
  const keys = Object.keys(record(recipe.item_layer_types) ?? recipe).filter(key => key.startsWith('requires_skill_tier_'));
  if (!keys.length) return true;
  const skills = record(record(record(caster.computed)?.creature)?.skills) ?? record(caster.skills);
  if (!skills) return null;
  for (const key of keys) {
    const level = Math.trunc(Number(key.slice('requires_skill_tier_'.length))) || 0;
    let met = false;
    for (const entry of read.entries(recipe, key)) {
      const row = record(entry);
      const id = upper(typeof entry === 'string' ? entry : record(row?.items)?.item_id ?? row?.item_id ?? row?.item_layer_effect);
      if (!UUID.test(id)) continue;
      const variable = SKILL_VAR_BY_ID.get(id);
      const tier = variable ? Math.trunc(Number(scalar(record(skills[variable])?.tier_level) ?? 0)) || 0 : 0;
      if (variable && tier >= level) { met = true; break; }
    }
    if (!met) return false;
  }
  return true;
}

/** get_item_id_name() through the page's catalog; null when unresolvable. */
function itemName(read: CraftStartReaders, id: string): string | null {
  const def = read.resolveItem ? read.resolveItem(id) : null;
  const name = scalar(record(def?.items)?.item_name ?? def?.item_name);
  if (typeof name === 'string' && name !== '') return name;
  const fetched = read.name ? read.name(upper(id)) : null;
  return typeof fetched === 'string' && fetched !== '' ? fetched : null;
}

const children = (row: Row): Row[] => {
  const nested = row.inventory_in_container ?? row.container_contents;
  return Array.isArray(nested) ? nested.flatMap(entry => { const child = record(entry); return child ? [child] : []; }) : [];
};

/**
 * ft_reagent_stock_resolution(): ONE whole stack with the required item and matching template, no size override and at least `qty` units.
 * A plain requirement also accepts a size-fit-only template; every other template retains exact identity.
 * Carried contents qualify recursively (first match in tree order); then — out of combat only — the encounter floor, where a stack is
 * usable only within reach of its OUTER container. 'unknown' = the page cannot measure a floor match's reach.
 */
function resolveStock(read: CraftStartReaders, caster: Row, encounter: Row | null, requirement: CraftManifestRow, outOfCombat: boolean)
  : { status: 'ok'; row: Row; floor: boolean } | { status: 'missing' | 'encounter_out_of_reach' | 'unknown' } {
  const wantedId = upper(requirement.id);
  const qtyOf = (row: Row): number => {
    const qty = read.inventoryField(row, 'item_qty');
    // inventory_field(): a lean row elides item_qty = 1
    if (qty === null || qty === undefined || qty === '') return UUID.test(String(read.inventoryField(row, 'inventory_id') ?? '')) ? 1 : 0;
    return Math.trunc(Number(scalar(qty))) || 0;
  };
  const deleted = (row: Row): boolean => { const d = scalar(read.inventoryField(row, 'inventory_deleted')); return !!d && d !== '0' && d !== 0; };
  const matches = (row: Row): boolean => upper(read.inventoryField(row, 'inventory_item_id')) === wantedId
    && reagentTemplateMatches(read.inventoryField(row, 'enchantment_template_id'), requirement.template)
    && (() => { const size = scalar(read.inventoryField(row, 'inventory_size')); return size === null || size === undefined || size === '' || size === 0 || size === '0'; })()
    && qtyOf(row) >= requirement.qty
    && UUID.test(String(read.inventoryField(row, 'inventory_id') ?? ''));
  let unreachable = false;
  let unknown = false;
  const walk = (rows: Row[], floor: boolean, container: Row | null): Row | null => {
    for (const row of rows) {
      if (deleted(row)) continue;
      const position = container ?? row;
      if (matches(row)) {
        if (!floor) return row;
        const near = withinReach(caster, position, read);
        if (near === true) return row;
        if (near === null) unknown = true; else unreachable = true;
      }
      const found = walk(children(row), floor, floor ? position : null);
      if (found) return found;
    }
    return null;
  };
  const carried = Array.isArray(caster.inventory) ? caster.inventory.flatMap(entry => { const row = record(entry); return row ? [row] : []; }) : null;
  if (carried === null) return { status: 'unknown' };
  const inHand = walk(carried, false, null);
  if (inHand) return { status: 'ok', row: inHand, floor: false };
  if (outOfCombat) {
    const floorRaw = encounter?.encounter_inventories_arr ?? caster.encounter_inventory;
    if (!Array.isArray(floorRaw)) return { status: 'unknown' };
    const onFloor = walk(floorRaw.flatMap(entry => { const row = record(entry); return row ? [row] : []; }), true, null);
    if (onFloor) return { status: 'ok', row: onFloor, floor: true };
    if (unknown) return { status: 'unknown' };
  }
  return { status: unreachable ? 'encounter_out_of_reach' : 'missing' };
}

/** crafting_roll()'s START arm (PHP order) + delete_reagents()'s row picks. */
export function resolveCraftStart(input: CraftStartInput, read: CraftStartReaders): CraftStartVerdict {
  const { caster, recipe, encounter } = input;
  const handback = (reason: string): CraftStartVerdict => ({ kind: 'handback', reason: `crafting_start:${reason}` });
  const refuse = (message: string): CraftStartVerdict => ({ kind: 'refuse', message });
  if (!isCraftStart(read, recipe, input.variable1)) return handback('not-a-start');
  if (!encounter) return handback('encounter-not-held');
  // ft_creature_in_combat()
  if (read.inCombat(encounter, caster)) return refuse(CRAFT_START_MESSAGES.inCombat);
  // has_status_effect(crafting)
  if (read.statuses(caster).some(status => upper(status.status_item_id ?? record(record(status.status_arr)?.items)?.item_id) === STATUS_CRAFTING))
    return refuse(CRAFT_START_MESSAGES.alreadyCrafting);
  // check_skill_requirements()
  const trained = skillRequirementsMet(read, recipe, caster);
  if (trained === null) return handback('skills-not-held');
  if (!trained) return refuse(CRAFT_START_MESSAGES.untrained);
  // a retired (archived / deleted) recipe never starts
  const flag = (key: string): boolean => { const v = scalar(record(recipe.items)?.[key] ?? recipe[key]); return !!v && v !== '0' && v !== 0; };
  if (flag('item_archived') || flag('item_deleted')) return refuse(CRAFT_START_MESSAGES.retired);
  // ft_campaign_context(): the crafter's encounter must belong to a campaign (the tracker runs on its clock)
  const master = upper(record(encounter.encounters)?.encounter_master_parent_id ?? encounter.encounter_master_parent_id);
  if (!UUID.test(master)) return refuse(CRAFT_START_MESSAGES.campaign);
  // ft_crafting_wip_required_seconds() / ft_crafting_wip_build_manifest() / ft_crafting_build_plan()
  const requested = typeof input.requestedSize === 'string' && input.requestedSize.trim() !== '' ? input.requestedSize : null;
  const plan = craftingPlan(recipe, requested ?? '', read);
  if (plan === null) return handback('plan-output-not-held');
  if ('refuse' in plan) {
    if (plan.refuse === 'time') return refuse(CRAFT_START_MESSAGES.time);
    if (plan.refuse === 'manifest') return refuse(CRAFT_START_MESSAGES.manifest);
    return refuse(requested !== null ? CRAFT_START_MESSAGES.size : CRAFT_START_MESSAGES.output);
  }
  // Universal Crafter: the untemplated requirements are waived before anything is resolved.
  let manifest = plan.manifest;
  let waived: CraftManifestRow[] = [];
  const split = waiveNonmagicalRequirements(manifest);
  if (split.waived.length) {
    const waives = craftingWaivesNonmagicalReagents(read, caster);
    if (waives === null) return handback('waiver-provider-not-held');
    if (waives) { manifest = split.kept; waived = split.waived; }
  }
  // delete_reagents(): resolve EVERY requirement before anything is consumed; the first miss refuses the whole start.
  const consume: CraftConsumeRow[] = [];
  for (const requirement of manifest) {
    const stock = resolveStock(read, caster, encounter, requirement, true);
    if (stock.status === 'unknown') return handback('reagent-stock-not-held');
    if (stock.status !== 'ok') {
      const name = itemName(read, requirement.id);
      if (name === null) return handback('reagent-name-not-held');
      return refuse(`You are missing an eligible required reagent: ${requirement.qty} × ${name}`);
    }
    consume.push({ inventory_id: upper(read.inventoryField(stock.row, 'inventory_id')), qty: requirement.qty, item_uuid: requirement.id,
      template_id: requirement.template, floor: stock.floor });
  }
  // A size-fit stack can match both a plain and an explicit size-fit requirement: one row still pays only one requirement.
  if (new Set(consume.map(row => row.inventory_id)).size !== consume.length) return handback('reagent-stack-shared');
  return { kind: 'start', plan: { ...plan, manifest }, consume, waived };
}

/** Shared stock reader for the browser recipe table and craft start. */
export { resolveStock as resolveCraftingStock };
